import { MediaItem } from '../../api/structs/media';
import { friendlyRenderError, persistenceStageLabel } from './persistenceLabels';

/**
 * A regeneration that FAILED, read off the canonical media item.
 *
 * Why these keys and not `persistence_error`: a regenerate never touches the
 * item the user is looking at. The provider call goes into a throwaway variant
 * (backend: RegenerateIntoVariant) which is deleted the moment it fails, so the
 * reason used to die with it and the storyboard re-rendered byte-identical —
 * one green "Regeneration started" toast and then silence, forever.
 *
 * The backend now copies the reason onto the CANONICAL item under these three
 * keys before dropping the variant. They are deliberately separate from
 * `persistence_*`, which describe the health of an item's OWN bytes: those
 * bytes are fine here (the previous take is still playing), and marking the
 * item persistence-failed would make every downstream "is this broken?" reader
 * agree that it is.
 *
 * Keep in sync with contentgen-go-backend internal/api/media
 * regenerateVariants.go (MetadataLastRegenerate*Key).
 */
export const REGENERATE_ERROR_KEY = 'last_regenerate_error';
export const REGENERATE_ERROR_STAGE_KEY = 'last_regenerate_error_stage';
export const REGENERATE_FAILED_AT_KEY = 'last_regenerate_failed_at';

/**
 * All three keys as a set — for the metadata editors that must NOT offer them
 * as raw editable fields (they are backend bookkeeping with a dedicated UI).
 */
export const REGENERATE_METADATA_KEYS = new Set<string>([
  REGENERATE_ERROR_KEY,
  REGENERATE_ERROR_STAGE_KEY,
  REGENERATE_FAILED_AT_KEY,
]);

export interface RegenerateFailure {
  /** Provider message, already truncated to 500 chars by the backend. */
  message: string;
  /** Stage id, e.g. `provider_generation`. */
  stage: string;
  /** Unix millis; 0 when the backend sent no stamp (older rows). */
  failedAt: number;
}

const toNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Reads the last failed regeneration off an item, or null when it has none. */
export const readRegenerateFailure = (
  item: MediaItem | undefined | null
): RegenerateFailure | null => {
  const metadata = item?.metadata;
  if (!metadata) return null;
  const message = String(metadata[REGENERATE_ERROR_KEY] ?? '').trim();
  const stage = String(metadata[REGENERATE_ERROR_STAGE_KEY] ?? '').trim();
  const failedAt = toNumber(metadata[REGENERATE_FAILED_AT_KEY]);
  // The stamp is written as a set, but a row that carries only one half is
  // still a failure the user must see — the message is the load-bearing part.
  if (!message && !stage) return null;
  return { message, stage, failedAt };
};

/**
 * One line: what failed, and why. The stage names the step, the message carries
 * the provider's own words (an RAI block reads "video blocked by Vertex safety
 * filter: … (support code 15236754)") — `friendlyRenderError` only rewrites the
 * persistence stages whose raw text says nothing.
 */
export const regenerateFailureText = (failure: RegenerateFailure): string => {
  const detail = friendlyRenderError(failure.message);
  const stage = failure.stage ? persistenceStageLabel(failure.stage) : '';
  return stage ? `${stage} failed — ${detail}` : `Regeneration failed — ${detail}`;
};

/**
 * True when `failure` is newer than the stamp seen when a regenerate was
 * started — i.e. THIS attempt is the one that failed, not a previous one whose
 * obituary is still on the item.
 *
 * An item with no earlier stamp (`since` undefined) treats any failure as new.
 * Equal timestamps count as OLD: the backend writes the stamp once per attempt,
 * so a value that has not moved cannot describe an attempt started after it.
 */
export const isNewerFailure = (
  failure: RegenerateFailure | null,
  since: number | undefined
): boolean => {
  if (!failure) return false;
  if (since === undefined) return true;
  return failure.failedAt > since;
};
